import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '../../components/Header';
import { BottomNav } from '../../components/BottomNav';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Check, AlertCircle, Plus, MapPin, Phone, Pencil, Trash2, X, Eye, EyeOff, Lock, Mail, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { vendedor: 'Vendedor', repartidor: 'Repartidor', admin: 'Administrador', propietario: 'Propietario' };
const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  propietario: 'bg-amber-100 text-amber-700',
  repartidor: 'bg-blue-100 text-blue-700',
  vendedor: 'bg-green-100 text-green-700',
};

export function AdminUsuarios() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'vendedor', telefono: '' });
  const [creating, setCreating] = useState(false);
  const [showCreatePw, setShowCreatePw] = useState(false);

  // Edit
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [showStoredPw, setShowStoredPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, puntosRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/puntos-venta`),
      ]);
      setUsers(usersRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setPuntosVenta(puntosRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setError('');
    } catch {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setNewUser({ email: '', password: '', name: '', role: 'vendedor', telefono: '' });
    setCreateError('');
    setShowCreatePw(false);
    setShowCreate(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setCreateError('');
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      setCreateError('Completa todos los campos requeridos');
      return;
    }
    setCreateError('');
    setCreating(true);
    try {
      await axios.post(`${API}/auth/register`, newUser);
      setShowCreate(false);
      setNewUser({ email: '', password: '', name: '', role: 'vendedor', telefono: '' });
      await loadData();
      toast.success('Usuario creado correctamente');
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const closeEdit = () => {
    setEditingUser(null);
    setPwForm({ newPassword: '', confirmPassword: '' });
    setShowStoredPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
    setError('');
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name,
      role: u.role,
      sueldo_diario: u.sueldo_diario ?? 0,
      telefono: u.telefono || '',
      punto_venta_id: u.punto_venta_id || '',
    });
    setPwForm({ newPassword: '', confirmPassword: '' });
    setShowStoredPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
    setError('');
  };

  const handleCancelEdit = () => {
    closeEdit();
    toast('Edición cancelada');
  };

  const handleSaveEdit = async () => {
    if (!editForm.name) { setError('El nombre es requerido'); return; }
    if (pwForm.newPassword || pwForm.confirmPassword) {
      if (pwForm.newPassword !== pwForm.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }
      if (pwForm.newPassword.length < 4) {
        setError('La contraseña debe tener al menos 4 caracteres');
        return;
      }
    }
    setError('');
    setSaving(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        ...editForm,
        sueldo_diario: parseFloat(editForm.sueldo_diario) || 0,
        punto_venta_id: editForm.punto_venta_id || null,
      });
      if (pwForm.newPassword) {
        await axios.patch(`${API}/users/${editingUser.id}/password`, { new_password: pwForm.newPassword });
      }
      closeEdit();
      await loadData();
      toast.success('Cambios guardados correctamente');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCredentials = (u) => {
    const pw = u.password_visible || '(sin contraseña registrada)';
    const texto = `Usuario: ${u.email}\nContraseña: ${pw}`;
    navigator.clipboard.writeText(texto).then(
      () => toast.success(`Credenciales de ${u.name} copiadas`),
      () => toast.error('No se pudo copiar al portapapeles'),
    );
  };

  const handleDelete = async (userId) => {
    setDeletingId(userId);
    try {
      await axios.delete(`${API}/users/${userId}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-usuarios-page">
      <Header title="Gestionar Usuarios" />

      <div className="page-container fade-in">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        <button
          onClick={openCreate}
          className="w-full h-16 bg-blue-600 text-white rounded-xl text-xl font-bold btn-press flex items-center justify-center gap-3 mb-6"
          data-testid="btn-add-user"
        >
          <Plus className="w-6 h-6" />
          Agregar Usuario
        </button>

        <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Usuarios ({users.length})
        </h3>

        <div className="space-y-3">
          {users.map((u) => {
            const punto = puntosVenta.find(p => p.id === u.punto_venta_id);
            const isMe = u.id === currentUser?.id;
            return (
              <div key={u.id} className="bg-white border-2 border-zinc-200 rounded-xl p-4" data-testid={`user-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-lg leading-tight">{u.name}</p>
                    <p className="text-zinc-500 text-sm truncate">{u.email}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ROLE_COLORS[u.role] || 'bg-zinc-100 text-zinc-600'}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                      {u.sueldo_diario > 0 && (
                        <span className="text-xs text-zinc-400">${u.sueldo_diario}/día</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-zinc-400">
                      {u.telefono && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />{u.telefono}
                        </span>
                      )}
                      {punto && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{punto.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {currentUser?.role === 'propietario' && (
                      <button
                        onClick={() => handleCopyCredentials(u)}
                        className="w-9 h-9 flex items-center justify-center bg-blue-50 rounded-lg"
                        title="Copiar cuenta y contraseña"
                        data-testid={`btn-copy-credentials-${u.id}`}
                      >
                        <Copy className="w-4 h-4 text-blue-500" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(u)}
                      className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg"
                      data-testid={`btn-edit-user-${u.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-600" />
                    </button>
                    {!isMe && (
                      <button
                        onClick={() => setConfirmingDelete(u.id)}
                        className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-lg"
                        data-testid={`btn-delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                </div>
              {/* Delete confirmation */}
              {confirmingDelete === u.id && (
                <div className="mt-3 pt-3 border-t border-red-100 flex items-center justify-between gap-3">
                  <span className="text-sm text-red-700 font-medium">¿Eliminar a {u.name}?</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="px-3 h-8 bg-zinc-100 rounded-lg text-sm font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => { setConfirmingDelete(null); handleDelete(u.id); }}
                      disabled={deletingId === u.id}
                      className="px-3 h-8 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center gap-1"
                      data-testid={`btn-confirm-delete-user-${u.id}`}
                    >
                      {deletingId === u.id
                        ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : null}
                      Sí, eliminar
                    </button>
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="modal-overlay" onClick={closeCreate}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-6">Nuevo Usuario</h2>
              {createError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />{createError}
                </div>
              )}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Nombre de la persona *</label>
                  <input type="text" value={newUser.name}
                    onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                    autoComplete="off"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-user-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Cuenta de acceso (correo) *</span>
                  </label>
                  <input type="email" value={newUser.email}
                    onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    autoComplete="username"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-user-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">
                    <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Contraseña *</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCreatePw ? 'text' : 'password'}
                      value={newUser.password}
                      onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Mínimo 4 caracteres"
                      autoComplete="new-password"
                      className="w-full h-14 px-4 pr-12 text-lg border-2 border-zinc-200 rounded-xl"
                      data-testid="input-user-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showCreatePw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Rol</label>
                  <select value={newUser.role}
                    onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="select-user-role"
                  >
                    <option value="vendedor">Vendedor</option>
                    <option value="repartidor">Repartidor</option>
                    <option value="admin">Administrador</option>
                    <option value="propietario">Propietario</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Teléfono WhatsApp</label>
                  <input type="tel" value={newUser.telefono}
                    onChange={e => setNewUser({ ...newUser, telefono: e.target.value })}
                    placeholder="Ej: 5512345678"
                    autoComplete="off"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-user-telefono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={closeCreate} className="h-14 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleCreateUser} disabled={creating}
                  className="h-14 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-create-user"
                >
                  {creating ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Crear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingUser && (
          <div className="modal-overlay" onClick={handleCancelEdit}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Editar Usuario</h2>
                <button onClick={handleCancelEdit} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Cuenta de acceso (solo lectura) */}
              <div className="mb-4 px-4 py-3 bg-zinc-50 border-2 border-zinc-200 rounded-xl flex items-center gap-3">
                <Mail className="w-5 h-5 text-zinc-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-zinc-400">Cuenta de acceso</p>
                  <p className="text-base font-semibold text-zinc-700 truncate">{editingUser.email}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Nombre de la persona *</label>
                  <input type="text" value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    autoComplete="off"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Rol</label>
                  <select value={editForm.role}
                    onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="select-edit-role"
                  >
                    <option value="vendedor">Vendedor</option>
                    <option value="repartidor">Repartidor</option>
                    <option value="admin">Administrador</option>
                    {currentUser?.role === 'propietario' && (
                      <option value="propietario">Propietario</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Sueldo diario ($)</label>
                  <input type="number" min="0" step="0.01" value={editForm.sueldo_diario}
                    onChange={e => setEditForm({ ...editForm, sueldo_diario: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-sueldo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Teléfono WhatsApp</label>
                  <input type="tel" value={editForm.telefono}
                    onChange={e => setEditForm({ ...editForm, telefono: e.target.value })}
                    placeholder="Ej: 5512345678"
                    autoComplete="off"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-telefono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Punto de Venta</label>
                  <select value={editForm.punto_venta_id}
                    onChange={e => setEditForm({ ...editForm, punto_venta_id: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="select-edit-punto"
                  >
                    <option value="">Sin asignar</option>
                    {puntosVenta.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Sección propietario: ver contraseña + cambiar contraseña */}
                {currentUser?.role === 'propietario' && (
                  <div className="border-t border-zinc-200 pt-4 space-y-3">
                    <p className="text-sm font-bold text-zinc-600 flex items-center gap-1">
                      <Lock className="w-4 h-4" /> Contraseña
                    </p>

                    {/* Ver contraseña actual */}
                    {editingUser.password_visible !== undefined && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                        <p className="text-xs font-medium text-amber-600 mb-1">Contraseña actual</p>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-lg font-mono font-bold text-amber-800 tracking-widest">
                            {showStoredPw
                              ? (editingUser.password_visible || <span className="text-amber-400 text-sm font-sans font-normal">No registrada</span>)
                              : '••••••••'}
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowStoredPw(v => !v)}
                            className="shrink-0 text-amber-500 hover:text-amber-700"
                          >
                            {showStoredPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Asignar nueva contraseña */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-500 mb-1">Nueva contraseña</label>
                      <div className="relative">
                        <input
                          type={showNewPw ? 'text' : 'password'}
                          value={pwForm.newPassword}
                          onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                          placeholder="Dejar vacío para no cambiar"
                          autoComplete="new-password"
                          className="w-full h-14 px-4 pr-12 text-lg border-2 border-zinc-200 rounded-xl"
                          data-testid="input-edit-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPw(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                        >
                          {showNewPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-500 mb-1">Confirmar nueva contraseña</label>
                      <div className="relative">
                        <input
                          type={showConfirmPw ? 'text' : 'password'}
                          value={pwForm.confirmPassword}
                          onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                          placeholder="Repite la nueva contraseña"
                          autoComplete="new-password"
                          className={`w-full h-14 px-4 pr-12 text-lg border-2 rounded-xl ${
                            pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword
                              ? 'border-red-300 bg-red-50'
                              : pwForm.confirmPassword && pwForm.newPassword === pwForm.confirmPassword
                              ? 'border-green-400'
                              : 'border-zinc-200'
                          }`}
                          data-testid="input-edit-password-confirm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPw(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                        >
                          {showConfirmPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Las contraseñas no coinciden
                        </p>
                      )}
                      {pwForm.confirmPassword && pwForm.newPassword === pwForm.confirmPassword && pwForm.newPassword && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Las contraseñas coinciden
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={handleCancelEdit} className="h-14 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleSaveEdit} disabled={saving}
                  className="h-14 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-save-user"
                >
                  {saving ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
